import React, { useEffect, useState } from "react";
import { Plus, ReceiptIndianRupee, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { PageHeader } from "@/components/Layout";
import { Chip, EmptyState } from "@/components/Bits";
import { formatINR } from "@/lib/ui";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-[#050505] border border-white/10 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white focus:ring-1 focus:ring-white transition-colors";

function statusChip(s) {
  const l = (s || "").toLowerCase();
  if (l === "paid") return "bg-[#00FF66]/10 border-[#00FF66]/30 text-[#00FF66]";
  if (l === "partial") return "bg-[#FFD600]/10 border-[#FFD600]/30 text-[#FFD600]";
  return "bg-[#FF3B30]/10 border-[#FF3B30]/30 text-[#FF6259]";
}

export default function Billing() {
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client: "", service: "", amount: "", received: "", status: "Unpaid", date: "" });

  const load = () => api.get("/invoices").then((r) => setInvoices(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.client || !form.amount) return toast.error("Client and amount are required");
    try {
      await api.post("/invoices", { ...form, amount: Number(form.amount), received: Number(form.received || 0), date: form.date || new Date().toISOString() });
      toast.success("Invoice created");
      setOpen(false);
      setForm({ client: "", service: "", amount: "", received: "", status: "Unpaid", date: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/invoices/${id}`); load(); };

  const totalBilled = invoices.reduce((s, i) => s + (i.amount || 0), 0);
  const totalReceived = invoices.reduce((s, i) => s + (i.received || 0), 0);
  const outstanding = totalBilled - totalReceived;

  return (
    <div>
      <PageHeader
        title="Billing & Collections"
        subtitle="Retainers, project invoices & receivables"
        actions={
          <button data-testid="add-invoice-button" onClick={() => setOpen(true)} className="flex items-center gap-2 text-sm bg-white text-black font-medium px-4 py-2 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98]">
            <Plus size={16} /> New Invoice
          </button>
        }
      />
      <div className="p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: "Total Billed", value: totalBilled, accent: "text-white" },
            { label: "Received", value: totalReceived, accent: "text-[#00FF66]" },
            { label: "Outstanding", value: outstanding, accent: "text-[#FF6259]" },
          ].map((s) => (
            <div key={s.label} className="bg-[#121212] border border-white/10 rounded-sm p-5">
              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500">{s.label}</div>
              <div className={`font-display text-3xl font-bold mt-2 ${s.accent}`}>{formatINR(s.value)}</div>
            </div>
          ))}
        </div>

        {invoices.length === 0 ? (
          <EmptyState icon={ReceiptIndianRupee} title="No invoices yet" hint="Create your first invoice." />
        ) : (
          <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-white/10 bg-[#0a0a0a]">
                  {["Client", "Service", "Amount", "Received", "Balance", "Status", ""].map((h) => (
                    <th key={h} className="py-3 px-3 font-mono text-[11px] uppercase tracking-wider text-zinc-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="border-b border-white/5 hover:bg-white/5 transition-colors group">
                    <td className="py-3 px-3 text-zinc-200">{inv.client}</td>
                    <td className="py-3 px-3 text-zinc-400">{inv.service}</td>
                    <td className="py-3 px-3 font-mono text-zinc-200">{formatINR(inv.amount)}</td>
                    <td className="py-3 px-3 font-mono text-[#00FF66]">{formatINR(inv.received)}</td>
                    <td className="py-3 px-3 font-mono text-[#FF6259]">{formatINR(inv.balance)}</td>
                    <td className="py-3 px-3"><Chip className={statusChip(inv.status)}>{inv.status}</Chip></td>
                    <td className="py-3 px-3">
                      <button data-testid={`delete-invoice-${inv.id}`} onClick={() => remove(inv.id)} className="text-zinc-600 hover:text-[#FF6259] opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white rounded-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">New Invoice</DialogTitle><DialogDescription className="sr-only">Create a new client invoice in INR.</DialogDescription></DialogHeader>
          <div className="space-y-3 pt-2">
            <input data-testid="invoice-client-input" className={inputCls} placeholder="Client *" value={form.client} onChange={(e) => setForm((f) => ({ ...f, client: e.target.value }))} />
            <input className={inputCls} placeholder="Service" value={form.service} onChange={(e) => setForm((f) => ({ ...f, service: e.target.value }))} />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="invoice-amount-input" type="number" className={inputCls} placeholder="Amount (₹) *" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
              <input type="number" className={inputCls} placeholder="Received (₹)" value={form.received} onChange={(e) => setForm((f) => ({ ...f, received: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select className={inputCls} value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                {["Unpaid", "Partial", "Paid"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input type="date" className={inputCls} value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
            </div>
            <button data-testid="invoice-submit-button" onClick={submit} className="w-full text-sm bg-white text-black font-medium py-2.5 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98]">Create Invoice</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
